/**
 * MapView — the one map surface screens are allowed to render (§6.5, §14.4).
 *
 * - Engine comes from MapEngineContext (default: lazy Leaflet+OSM chunk, §11.2).
 * - Shows a skeleton until the provider chunk arrives; never blocks the route.
 * - Theming: dark mode re-skins tiles/chrome purely in CSS (map.css) off the
 *   same `data-theme` / `prefers-color-scheme` signals as ThemeProvider —
 *   zero JS listeners, instant on theme switch.
 * - RTL: reads the computed direction at mount and hands it to the provider,
 *   which mirrors its controls (§14.2).
 * - Resize: throttled ResizeObserver → invalidateSize (§11.2).
 */

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { cx } from "../utils/cx";
import { useMapEngine } from "./MapEngineContext";
import type { LatLng, MapProviderInterface, MapViewState } from "./types";

const RESIZE_THROTTLE_MS = 150;

export interface MapViewProps {
  /** Initial center — later moves go through the imperative handle / onReady map. */
  readonly initialCenter: LatLng;
  /** Initial zoom. Default 13 (city district scale). */
  readonly initialZoom?: number;
  readonly minZoom?: number;
  readonly maxZoom?: number;
  /** false = static, non-interactive display map. Default true. */
  readonly interactive?: boolean;
  /** Accessible name for the map region — REQUIRED (§14.3). Localized by the caller. */
  readonly label: string;
  /** Fired once the provider is live. THE way to get the MapProviderInterface. */
  readonly onReady?: (map: MapProviderInterface) => void;
  /** Click/tap on the base map (not on markers). */
  readonly onMapClick?: (position: LatLng) => void;
  /** Pan/zoom settled — providers throttle this (§11.2). */
  readonly onMoveEnd?: (view: MapViewState) => void;
  /** Overlay content rendered above the canvas (legends, hint chips). */
  readonly children?: ReactNode;
  readonly className?: string;
  /** Height of the map block. Default var(--ui-map-height, 20rem). */
  readonly height?: string | number;
}

export interface MapViewHandle {
  /** null until onReady has fired. */
  getMap(): MapProviderInterface | null;
}

export const MapView = forwardRef<MapViewHandle, MapViewProps>(function MapView(
  {
    initialCenter,
    initialZoom = 13,
    minZoom,
    maxZoom,
    interactive = true,
    label,
    onReady,
    onMapClick,
    onMoveEnd,
    children,
    className,
    height,
  },
  ref,
) {
  const engine = useMapEngine();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapProviderInterface | null>(null);
  const [ready, setReady] = useState(false);

  // Latest-ref pattern: rerenders with new callbacks never rewire the provider.
  const callbacksRef = useRef({ onReady, onMapClick, onMoveEnd });
  callbacksRef.current = { onReady, onMapClick, onMoveEnd };

  const initialRef = useRef({ center: initialCenter, zoom: initialZoom, minZoom, maxZoom, interactive });

  useImperativeHandle(ref, () => ({ getMap: () => mapRef.current }), []);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    if (!wrapper || !canvas) return;
    let disposed = false;
    let map: MapProviderInterface | null = null;
    let observer: ResizeObserver | null = null;
    let resizeTimer: ReturnType<typeof setTimeout> | null = null;

    const init = initialRef.current;
    // Nearest dir attribute wins (matches HTML inheritance and works in jsdom);
    // computed style is the fallback for CSS-set direction.
    const dirAttr = wrapper.closest("[dir]")?.getAttribute("dir")?.toLowerCase();
    const dir: "ltr" | "rtl" =
      dirAttr === "rtl" || (dirAttr !== "ltr" && getComputedStyle(wrapper).direction === "rtl")
        ? "rtl"
        : "ltr";

    void engine
      .load()
      .then((create) => {
        if (disposed) return;
        map = create(canvas, {
          view: { center: init.center, zoom: init.zoom },
          dir,
          ...(init.minZoom !== undefined ? { minZoom: init.minZoom } : {}),
          ...(init.maxZoom !== undefined ? { maxZoom: init.maxZoom } : {}),
          interactive: init.interactive,
        });
        mapRef.current = map;

        map.on("click", (position) => callbacksRef.current.onMapClick?.(position));
        map.on("moveend", (view) => callbacksRef.current.onMoveEnd?.(view));

        // Throttled resize → invalidateSize (§11.2).
        if (typeof ResizeObserver !== "undefined") {
          observer = new ResizeObserver(() => {
            if (resizeTimer !== null) return;
            resizeTimer = setTimeout(() => {
              resizeTimer = null;
              mapRef.current?.invalidateSize();
            }, RESIZE_THROTTLE_MS);
          });
          observer.observe(wrapper);
        }

        setReady(true);
        callbacksRef.current.onReady?.(map);
      })
      .catch(() => {
        /* provider chunk failed (offline) — skeleton stays; screens keep working */
      });

    return () => {
      disposed = true;
      if (resizeTimer !== null) clearTimeout(resizeTimer);
      observer?.disconnect();
      mapRef.current = null;
      map?.destroy();
      setReady(false);
    };
  }, [engine]);

  const style: CSSProperties = {};
  if (height !== undefined) style.blockSize = height;

  return (
    <div
      ref={wrapperRef}
      className={cx("ui-map", !ready && "ui-map--loading", className)}
      style={style}
      role="region"
      aria-label={label}
      aria-busy={!ready}
    >
      <div ref={canvasRef} className="ui-map__stage" />
      {!ready && <span className="ui-skeleton ui-skeleton--rect ui-map__skeleton" aria-hidden="true" />}
      {children !== undefined && <div className="ui-map__overlay">{children}</div>}
    </div>
  );
});
